/*
 * Copyright (c) 2024 Étienne FOLIO
 * Tous droits réservés.
 *
 * Ce fichier fait partie d'un logiciel propriétaire.
 * Toute reproduction, modification ou distribution, totale ou partielle,
 * de ce fichier est interdite sans l'autorisation écrite préalable
 * de l'auteur.
 */

// api.js

import axios from 'axios';

// Créer une instance Axios avec la configuration CSRF Token
const api = axios.create({
  baseURL: "/api/",
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Accept': 'application/json',
  }
});

const sessionProbeClient = axios.create({
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

let hasSessionExpiredNotification = false;
const sessionExpiredListeners = new Set();
let lastSessionProbeAt = 0;
let pendingSessionProbe = null;
const SESSION_PROBE_TTL_MS = 15000;

const isAuthEndpoint = (url = '') => {
  const normalizedUrl = String(url);
  return normalizedUrl.includes('login/') || normalizedUrl.includes('logout/');
};

const isSessionProbeExemptEndpoint = (url = "") => {
  const normalizedUrl = String(url);
  return (
    isAuthEndpoint(normalizedUrl) ||
    normalizedUrl.includes("ui-config/") ||
    normalizedUrl.includes("document-templates/history/") ||
    normalizedUrl.includes("document-templates/preview/") ||
    normalizedUrl.includes("release-notes/") ||
    normalizedUrl.includes("current-user/")
  );
};

const createBlockedSessionError = () => {
  const error = new Error("Session expired - request blocked");
  error.code = "SESSION_EXPIRED_BLOCKED";
  return error;
};

const extractErrorDetail = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    return data.toLowerCase();
  }
  if (typeof data?.detail === 'string') {
    return data.detail.toLowerCase();
  }
  return '';
};

const isSessionExpiredResponse = (error) => {
  const status = error?.response?.status;
  if (status === 401) {
    return true;
  }
  if (status !== 403) {
    return false;
  }

  const detail = extractErrorDetail(error);
  if (!detail) {
    return true;
  }

  return (
    detail.includes('authentication') ||
    detail.includes('authentification') ||
    detail.includes('credentials') ||
    detail.includes('csrf') ||
    detail.includes('session')
  );
};

const notifySessionExpired = () => {
  sessionExpiredListeners.forEach((listener) => {
    try {
      listener();
    } catch (e) {
      // Ignore listener errors to keep interceptor resilient.
    }
  });
};

export const onSessionExpired = (listener) => {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
};

export const clearSessionExpiredState = () => {
  hasSessionExpiredNotification = false;
  lastSessionProbeAt = Date.now();
};

const isLikelyHtmlResponse = (response) => {
  const contentType = String(response?.headers?.["content-type"] || "").toLowerCase();
  if (contentType.includes("text/html")) {
    return true;
  }

  const payload = response?.data;
  if (typeof payload !== "string") {
    return false;
  }
  const trimmed = payload.trim().toLowerCase();
  return trimmed.startsWith("<!doctype html") || trimmed.startsWith("<html");
};

const isApiRequest = (url = "") => {
  const normalized = String(url || "");
  if (!normalized) {
    return true;
  }
  if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
    return normalized.includes("/api/");
  }
  if (normalized.startsWith("/")) {
    return normalized.startsWith("/api/");
  }
  // With axios baseURL="/api/", most calls are relative like "structure/".
  return true;
};

api.interceptors.response.use(
  (response) => {
    const requestUrl = response?.config?.url || '';

    if (
      isApiRequest(requestUrl) &&
      !isAuthEndpoint(requestUrl) &&
      isLikelyHtmlResponse(response)
    ) {
      if (!hasSessionExpiredNotification) {
        hasSessionExpiredNotification = true;
        notifySessionExpired();
      }
      return Promise.reject(new Error("Session expired"));
    }

    if (String(requestUrl).includes('login/')) {
      clearSessionExpiredState();
    }
    return response;
  },
  (error) => {
    const requestUrl = error?.config?.url || '';

    if (isSessionExpiredResponse(error) && !isAuthEndpoint(requestUrl)) {
      if (!hasSessionExpiredNotification) {
        hasSessionExpiredNotification = true;
        notifySessionExpired();
      }
    }

    return Promise.reject(error);
  }
);

const ensureSessionAlive = async () => {
  const now = Date.now();
  if (now - lastSessionProbeAt < SESSION_PROBE_TTL_MS) {
    return;
  }

  if (!pendingSessionProbe) {
    pendingSessionProbe = sessionProbeClient
      .get("/api/current-user/")
      .then(() => {
        lastSessionProbeAt = Date.now();
      })
      .catch((error) => {
        if (!hasSessionExpiredNotification) {
          hasSessionExpiredNotification = true;
          notifySessionExpired();
        }
        throw error;
      })
      .finally(() => {
        pendingSessionProbe = null;
      });
  }

  await pendingSessionProbe;
};

export const probeSessionAlive = async () => {
  if (hasSessionExpiredNotification) {
    throw createBlockedSessionError();
  }
  await ensureSessionAlive();
  return true;
};

api.interceptors.request.use(
  async (config) => {
    const requestUrl = config?.url || "";
    if (hasSessionExpiredNotification && !isSessionProbeExemptEndpoint(requestUrl)) {
      return Promise.reject(createBlockedSessionError());
    }

    if (!isSessionProbeExemptEndpoint(requestUrl)) {
      try {
        await ensureSessionAlive();
      } catch (error) {
        return Promise.reject(createBlockedSessionError());
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

export default api;
